import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, apiPost } from "@/lib/api";
import { grSovKeys } from "@/lib/query";

// ── Types ──

export interface GrSovLine {
  id: string;
  commitmentId: string;
  itemNo: string | null;
  description: string;
  scheduledValue: string | null;
  costCodeId: string | null;
  costCodeCode: string | null;
  costCodeDescription: string | null;
  lineCategory: string;
  retentionPercentOverride: string | null;
}

export interface GrVarianceLine {
  id: string;
  itemNo: string | null;
  description: string;
  costCodeId: string | null;
  scheduledValue: string | null;
  approvedTotal: string;
  remaining: string;
  isOverBudget: boolean;
}

export interface GrSovLineInput {
  id?: string;
  commitmentId: string;
  itemNo: string;
  description: string;
  costCodeId: string;
  scheduledValue: string;
  retentionPercentOverride?: string;
}

// ── Hooks ──

export function useGrSov(projectId: string) {
  return useQuery({
    queryKey: grSovKeys.list(projectId),
    queryFn: () => apiFetch<GrSovLine[]>(`/projects/${projectId}/gr-sov`),
    enabled: !!projectId,
  });
}

export function useUpdateGrSov(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lines: GrSovLineInput[]) =>
      apiPost<GrSovLine[]>(`/projects/${projectId}/gr-sov`, { lines }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: grSovKeys.list(projectId) });
      qc.invalidateQueries({ queryKey: grSovKeys.variance(projectId) });
      toast.success("GR SOV saved");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useGrVariance(projectId: string) {
  return useQuery({
    queryKey: grSovKeys.variance(projectId),
    queryFn: () => apiFetch<GrVarianceLine[]>(`/projects/${projectId}/gr-variance`),
    enabled: !!projectId,
  });
}
